import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import Cookies from 'js-cookie';
import '../styles/paymentTopup.css';
import Notification from '../components/Notification';
import { UserContext } from '../contexts/UserContext';

// Import provider images
import mpesaImage from '../assets/money-transfer/m-pesa.png';
import airtelImage from '../assets/money-transfer/airtal-money.png';
import orangeImage from '../assets/money-transfer/orange-money.png';
import afrimoneyImage from '../assets/money-transfer/afrimoney.png';

// Mobile Money Provider Detection (Congo DRC)
const PROVIDER_PREFIXES = {
    mpesa: ['81', '82', '83'],           // Vodacom M-Pesa
    airtel: ['84', '85', '86', '89', '90', '91', '97', '99'], // Airtel Money
    orange: ['80'],                       // Orange Money
    afrimoney: ['98']                     // Africell Money
};

const PROVIDER_NAMES = {
    mpesa: 'Vodacom M-Pesa',
    airtel: 'Airtel Money',
    orange: 'Orange Money',
    afrimoney: 'Africell Money'
};

const PROVIDER_INSTRUCTIONS = {
    mpesa: [
        'Vous allez recevoir une notification M-Pesa',
        'Ouvrez le menu USSD sur votre téléphone',
        'Entrez votre code PIN M-Pesa',
        'Confirmez le paiement'
    ],
    airtel: [
        'Vous allez recevoir une notification Airtel Money',
        'Composez *501# sur votre téléphone',
        'Entrez votre code PIN Airtel Money',
        'Confirmez le paiement'
    ],
    orange: [
        'Vous allez recevoir une notification Orange Money',
        'Composez #150# sur votre téléphone',
        'Entrez votre code PIN Orange Money',
        'Confirmez le paiement'
    ],
    afrimoney: [
        'Vous allez recevoir une notification Africell Money',
        'Ouvrez le menu sur votre téléphone',
        'Entrez votre code PIN Africell Money',
        'Confirmez le paiement'
    ]
};

const PaymentTopup = () => {
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const [amount, setAmount] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [useRegisteredPhone, setUseRegisteredPhone] = useState(true);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({ message: '', type: '', header: '' });
    const [paymentResponse, setPaymentResponse] = useState(null);
    const [checkingStatus, setCheckingStatus] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [pollCount, setPollCount] = useState(0);
    const [provider, setProvider] = useState(null);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [phoneError, setPhoneError] = useState('');
    const pollIntervalRef = useRef(null);
    const timeoutRef = useRef(null);

    // Constants
    const USD_TO_ZAIRES = 1; // 1 USD = 1 Zaire (no conversion)
    const TRANSACTION_FEE_PERCENT = 3.5;
    const MIN_AMOUNT = 1;
    const MAX_AMOUNT = 500;
    const MAX_POLLS = 120; // 10 minutes (120 * 5 seconds)

    // Format user phone to 243XXXXXXXXX
    const formatUserPhone = (phone) => {
        if (!phone) return '';
        let formattedPhone = phone.replace(/[\s\-+]/g, '');
        if (formattedPhone.startsWith('0')) {
            formattedPhone = '243' + formattedPhone.substring(1);
        } else if (!formattedPhone.startsWith('243')) {
            formattedPhone = '243' + formattedPhone;
        }
        return formattedPhone;
    };

    // Pre-fill phone number from user profile
    useEffect(() => {
        if (user?.mobile_number && useRegisteredPhone) {
            setPhoneNumber(formatUserPhone(user.mobile_number));
        } else if (!useRegisteredPhone) {
            setPhoneNumber('');
        }
    }, [user?.mobile_number, useRegisteredPhone]);

    // Detect mobile money provider from phone number
    const detectProvider = (phone) => {
        const cleaned = phone.replace(/[\s\-+]/g, '');
        const prefix = cleaned.startsWith('243') ? cleaned.substring(3, 5) : cleaned.substring(0, 2);
        
        for (const [providerKey, prefixes] of Object.entries(PROVIDER_PREFIXES)) {
            if (prefixes.includes(prefix)) {
                return providerKey;
            }
        }
        return null;
    };

    // Validate phone number format
    const validatePhoneNumber = (phone) => {
        const cleaned = phone.replace(/[\s\-+]/g, '');
        
        // Must be 12 digits starting with 243
        if (!/^243[0-9]{9}$/.test(cleaned)) {
            return {
                valid: false,
                message: 'Le numéro doit commencer par 243 et contenir 12 chiffres (ex: 243828812498)'
            };
        }
        
        const detectedProvider = detectProvider(cleaned);
        if (!detectedProvider) {
            return {
                valid: false,
                message: 'Opérateur non reconnu. Utilisez Vodacom, Airtel, Orange ou Africell.'
            };
        }
        
        return { valid: true };
    };

    // Detect provider when phone number changes
    useEffect(() => {
        if (phoneNumber.length >= 5) {
            const detected = detectProvider(phoneNumber);
            setProvider(detected);
            
            // Validate phone number
            const validation = validatePhoneNumber(phoneNumber);
            if (!validation.valid && phoneNumber.length >= 10) {
                setPhoneError(validation.message);
            } else {
                setPhoneError('');
            }
        } else {
            setProvider(null);
            setPhoneError('');
        }
    }, [phoneNumber]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    // Format phone number for display
    const formatPhoneDisplay = (phone) => {
        const cleaned = phone.replace(/[\s\-+]/g, '');
        if (cleaned.length === 12 && cleaned.startsWith('243')) {
            return `+${cleaned.slice(0, 3)} ${cleaned.slice(3, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8)}`;
        }
        return phone;
    };

    // Calculate values
    const calculateZaires = (amountValue) => {
        const amt = parseFloat(amountValue) || 0;
        return Math.floor(amt * USD_TO_ZAIRES);
    };

    const calculateFee = (amountValue) => {
        const amt = parseFloat(amountValue) || 0;
        return (amt * TRANSACTION_FEE_PERCENT / 100).toFixed(2);
    };

    const calculateTotal = (amountValue) => {
        const amt = parseFloat(amountValue) || 0;
        const fee = parseFloat(calculateFee(amountValue));
        return (amt + fee).toFixed(2);
    };

    // Validate amount
    const validateAmount = (value) => {
        const amt = parseFloat(value);
        if (isNaN(amt) || amt <= 0) {
            return { valid: false, message: 'Veuillez entrer un montant valide' };
        }
        if (amt < MIN_AMOUNT) {
            return { valid: false, message: `Le montant minimum est de $${MIN_AMOUNT}` };
        }
        if (amt > MAX_AMOUNT) {
            return { valid: false, message: `Le montant maximum est de $${MAX_AMOUNT}` };
        }
        return { valid: true };
    };

    // Progress messages based on elapsed time
    const getProgressMessage = (count) => {
        const seconds = count * 5;
        if (seconds < 10) return '📱 Vérifiez votre téléphone...';
        if (seconds < 20) return '🔐 Entrez votre code PIN...';
        if (seconds < 35) return '⏳ En attente de confirmation...';
        if (seconds < 50) return '🔄 Traitement en cours...';
        if (seconds < 70) return '📡 Communication avec l\'opérateur...';
        if (seconds < 90) return '✅ Validation du paiement...';
        if (seconds < 120) return '🔍 Vérification finale...';
        const minutes = Math.floor(seconds / 60);
        return `⏱️ Attente prolongée (${minutes}min)...`;
    };

    const handleInitiatePayment = async (e) => {
        e.preventDefault();
        
        // Validate amount
        const amountValidation = validateAmount(amount);
        if (!amountValidation.valid) {
            setNotification({
                message: amountValidation.message,
                type: 'error',
                header: 'Montant Invalide'
            });
            return;
        }

        // Validate phone number
        const phoneValidation = validatePhoneNumber(phoneNumber);
        if (!phoneValidation.valid) {
            setNotification({
                message: phoneValidation.message,
                type: 'error',
                header: 'Numéro Invalide'
            });
            return;
        }

        // Double check provider is detected
        if (!provider) {
            setNotification({
                message: 'Opérateur mobile non reconnu. Vérifiez votre numéro.',
                type: 'error',
                header: 'Erreur'
            });
            return;
        }

        setLoading(true);
        const token = Cookies.get('authToken');

        if (!token) {
            setNotification({
                message: 'Vous devez être connecté pour effectuer un paiement',
                type: 'error',
                header: 'Non Connecté'
            });
            setLoading(false);
            return;
        }

        try {
            // Clean phone number
            const cleanedPhone = phoneNumber.replace(/[\s\-+]/g, '');
            
            const response = await fetch(`${config.backendUrl}/payments/mobile-payment/initiate/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`
                },
                body: JSON.stringify({
                    amount: parseFloat(calculateTotal(amount)), // Total to charge (with fees)
                    zaires_to_credit: parseFloat(amount), // Original amount = Zaires to credit
                    phone_number: cleanedPhone,
                    currency: 'USD',
                    payment_method: selectedProvider || provider || 'mpesa'
                })
            });

            const data = await response.json();

            if (response.ok) {
                setPaymentResponse(data);
                setNotification({
                    message: `Entrez votre code PIN sur ${formatPhoneDisplay(phoneNumber)}`,
                    type: 'info',
                    header: 'Paiement Initié'
                });
                
                // Start checking payment status
                startStatusCheck(data.transaction_id);
            } else {
                setNotification({
                    message: data.error || 'Échec de l\'initiation du paiement',
                    type: 'error',
                    header: 'Erreur'
                });
            }
        } catch (error) {
            setNotification({
                message: 'Erreur de connexion. Veuillez réessayer.',
                type: 'error',
                header: 'Erreur Réseau'
            });
        } finally {
            setLoading(false);
        }
    };

    const startStatusCheck = (transactionId) => {
        setCheckingStatus(true);
        setPollCount(0);
        let currentPollCount = 0;
        const token = Cookies.get('authToken');

        // Initial delay before starting polling
        setTimeout(() => {
            pollIntervalRef.current = setInterval(async () => {
                currentPollCount++;
                setPollCount(currentPollCount);
                setStatusMessage(getProgressMessage(currentPollCount));

                // Stop polling after max attempts
                if (currentPollCount >= MAX_POLLS) {
                    clearInterval(pollIntervalRef.current);
                    setCheckingStatus(false);
                    setNotification({
                        message: 'Timeout - le paiement n\'a pas été confirmé. Contactez le support si vous avez été débité.',
                        type: 'error',
                        header: 'Délai Dépassé'
                    });
                    return;
                }

                try {
                    const response = await fetch(
                        `${config.backendUrl}/payments/mobile-payment/status/${transactionId}/`,
                        {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Token ${token}`
                            }
                        }
                    );

                    if (response.ok) {
                        const data = await response.json();

                        if (data.status === 'SUCCESS') {
                            clearInterval(pollIntervalRef.current);
                            setCheckingStatus(false);
                            setNotification({
                                message: `Paiement réussi! ${data.zaires_credited || calculateZaires(amount).toLocaleString()} Zaires ont été crédités à votre compte.`,
                                type: 'success',
                                header: '✅ Paiement Réussi'
                            });
                            
                            // Redirect to wallet after 3 seconds
                            timeoutRef.current = setTimeout(() => {
                                navigate('/portefeuille');
                            }, 3000);
                        } else if (data.status === 'FAILED' || data.status === 'CANCELLED') {
                            clearInterval(pollIntervalRef.current);
                            setCheckingStatus(false);
                            setNotification({
                                message: data.message || 'Le paiement a échoué ou a été annulé.',
                                type: 'error',
                                header: 'Échec du Paiement'
                            });
                        }
                    }
                } catch (error) {
                    // Silent - polling continues
                    console.error('Error checking payment status:', error);
                }
            }, 5000); // Check every 5 seconds
        }, 2000); // Start polling after 2 seconds
    };

    const cancelPayment = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
        }
        setCheckingStatus(false);
        setPaymentResponse(null);
        setAmount('');
        setPhoneNumber(user?.mobile_number ? formatUserPhone(user.mobile_number) : '');
        setNotification({
            message: 'Paiement annulé',
            type: 'info',
            header: 'Annulé'
        });
    };

    const handleNotificationClose = () => {
        setNotification({ message: '', type: '', header: '' });
    };

    const predefinedAmounts = [5, 10, 20, 50];

    return (
        <div className="pt-page-container">
            {notification.message && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    header={notification.header}
                    onClose={handleNotificationClose}
                />
            )}

            <div className="pt-card">
                <button 
                    className="pt-back-btn"
                    onClick={() => navigate('/portefeuille')}
                >
                    ← Retour au Portefeuille
                </button>

                <div className="pt-header">
                    <h1>💳 Recharger votre Portefeuille</h1>
                    <p>Rechargez votre compte en Zaires via Mobile Money</p>
                </div>

                {!paymentResponse ? (
                    <form onSubmit={handleInitiatePayment} className="pt-form">
                        <div className="pt-form-group">
                            <label>Montant (USD)</label>
                            <input
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="Entrez le montant"
                                className="pt-input"
                                min={MIN_AMOUNT}
                                max={MAX_AMOUNT}
                                step="0.01"
                                required
                            />
                            <div className="pt-quick-amounts">
                                {predefinedAmounts.map(amt => (
                                    <button
                                        key={amt}
                                        type="button"
                                        className={`pt-quick-btn ${parseFloat(amount) === amt ? 'pt-active' : ''}`}
                                        onClick={() => setAmount(amt.toString())}
                                    >
                                        ${amt}
                                    </button>
                                ))}
                            </div>
                            <small className="pt-amount-limits">Min: ${MIN_AMOUNT} - Max: ${MAX_AMOUNT}</small>
                        </div>

                        {amount && parseFloat(amount) > 0 && (
                            <div className="pt-conversion">
                                <div className="pt-conversion-row">
                                    <span>Montant:</span>
                                    <strong>${parseFloat(amount).toFixed(2)}</strong>
                                </div>
                                <div className="pt-conversion-row pt-fee-row">
                                    <span>Frais de transaction ({TRANSACTION_FEE_PERCENT}%):</span>
                                    <strong>${calculateFee(amount)}</strong>
                                </div>
                                <div className="pt-conversion-row pt-total-row">
                                    <span>Total à payer:</span>
                                    <strong>${calculateTotal(amount)}</strong>
                                </div>
                                <div className="pt-conversion-row pt-zaires-row">
                                    <span>Vous recevrez:</span>
                                    <strong className="pt-zaires-amount">{calculateZaires(amount).toLocaleString()} Zaires</strong>
                                </div>
                                <small className="pt-rate-info">Taux: 1 USD = {USD_TO_ZAIRES.toLocaleString()} Zaires</small>
                            </div>
                        )}

                        {/* Payment Method Selection */}
                        <div className="pt-form-group">
                            <label>Payer avec</label>
                            <div className="pt-providers-container">
                                <button
                                    type="button"
                                    className={`pt-provider-card ${selectedProvider === 'mpesa' ? 'pt-provider-selected' : ''}`}
                                    onClick={() => setSelectedProvider('mpesa')}
                                >
                                    <img src={mpesaImage} alt="M-Pesa" className="pt-provider-image" />
                                    {selectedProvider === 'mpesa' && (
                                        <div className="pt-selected-badge">✓</div>
                                    )}
                                </button>
                                
                                <button
                                    type="button"
                                    className={`pt-provider-card ${selectedProvider === 'airtel' ? 'pt-provider-selected' : ''}`}
                                    onClick={() => setSelectedProvider('airtel')}
                                >
                                    <img src={airtelImage} alt="Airtel Money" className="pt-provider-image" />
                                    {selectedProvider === 'airtel' && (
                                        <div className="pt-selected-badge">✓</div>
                                    )}
                                </button>
                                
                                <button
                                    type="button"
                                    className={`pt-provider-card ${selectedProvider === 'orange' ? 'pt-provider-selected' : ''}`}
                                    onClick={() => setSelectedProvider('orange')}
                                >
                                    <img src={orangeImage} alt="Orange Money" className="pt-provider-image" />
                                    {selectedProvider === 'orange' && (
                                        <div className="pt-selected-badge">✓</div>
                                    )}
                                </button>
                                
                                <button
                                    type="button"
                                    className={`pt-provider-card ${selectedProvider === 'afrimoney' ? 'pt-provider-selected' : ''}`}
                                    onClick={() => setSelectedProvider('afrimoney')}
                                >
                                    <img src={afrimoneyImage} alt="Africell Money" className="pt-provider-image" />
                                    {selectedProvider === 'afrimoney' && (
                                        <div className="pt-selected-badge">✓</div>
                                    )}
                                </button>
                            </div>
                            {selectedProvider && (
                                <div className="pt-selected-provider-text">
                                    ✓ {PROVIDER_NAMES[selectedProvider]} sélectionné
                                </div>
                            )}
                        </div>

                        {/* Phone Number Selection */}
                        {user?.mobile_number && (
                            <div className="pt-phone-selection">
                                <label>Numéro de Téléphone</label>
                                <div className="pt-phone-options">
                                    <button
                                        type="button"
                                        className={`pt-phone-option-btn ${useRegisteredPhone ? 'pt-active' : ''}`}
                                        onClick={() => setUseRegisteredPhone(true)}
                                    >
                                        <span className="pt-option-title">Téléphone enregistré</span>
                                        <span className="pt-option-number">{formatPhoneDisplay(formatUserPhone(user.mobile_number))}</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`pt-phone-option-btn ${!useRegisteredPhone ? 'pt-active' : ''}`}
                                        onClick={() => setUseRegisteredPhone(false)}
                                    >
                                        <span className="pt-option-title">Utiliser un autre numéro</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Phone Number Input */}
                        {(!useRegisteredPhone || !user?.mobile_number) && (
                            <div className="pt-form-group">
                                <label>Numéro de Téléphone</label>
                                <div className={`pt-phone-wrapper ${provider ? 'pt-valid' : ''} ${phoneError ? 'pt-error' : ''}`}>
                                    <span className="pt-phone-icon">📱</span>
                                    <input
                                        type="tel"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        placeholder="243828812498"
                                        className="pt-input pt-phone-input"
                                        maxLength={12}
                                        required
                                    />
                                    {provider && (
                                        <span className="pt-provider-badge">{PROVIDER_NAMES[provider]}</span>
                                    )}
                                </div>
                                {phoneError && <small className="pt-error-text">{phoneError}</small>}
                                <small>Format: 12 chiffres commençant par 243</small>
                            </div>
                        )}

                        {/* Provider Detected Info */}
                        {provider && (
                            <div className="pt-provider-info">
                                <span className="pt-provider-detected">✓ {PROVIDER_NAMES[provider]} détecté</span>
                            </div>
                        )}

                        {/* Payment Instructions */}
                        {provider && (
                            <div className="pt-instructions-card">
                                <h3>📋 Instructions de paiement</h3>
                                <ol>
                                    {PROVIDER_INSTRUCTIONS[provider].map((instruction, index) => (
                                        <li key={index}>{instruction}</li>
                                    ))}
                                </ol>
                            </div>
                        )}

                        <button 
                            type="submit" 
                            className="pt-submit-btn"
                            disabled={loading || (!selectedProvider && !provider) || !amount || parseFloat(amount) <= 0}
                        >
                            {loading ? 'Traitement...' : (selectedProvider || provider) ? `Payer avec ${PROVIDER_NAMES[selectedProvider || provider]}` : 'Sélectionnez un opérateur'}
                        </button>
                        
                        <p className="pt-security-text">🔒 Paiement sécurisé via FreshPay PayDRC</p>
                    </form>
                ) : (
                    <div className="pt-instructions">
                        <div className="pt-success-icon">📱</div>
                        <h2>Paiement en Attente</h2>
                        <p className="pt-waiting-msg">Vérifiez votre téléphone et entrez votre code PIN</p>
                        
                        <div className="pt-details">
                            <p><strong>Transaction ID:</strong> {paymentResponse.transaction_id}</p>
                            <p><strong>Montant total:</strong> ${calculateTotal(amount)}</p>
                            <p><strong>Zaires à recevoir:</strong> {paymentResponse.expected_zaires || calculateZaires(amount).toLocaleString()}</p>
                            <p><strong>Téléphone:</strong> {formatPhoneDisplay(phoneNumber)}</p>
                            {provider && <p><strong>Opérateur:</strong> {PROVIDER_NAMES[provider]}</p>}
                        </div>

                        {checkingStatus && (
                            <div className="pt-status-checking">
                                <div className="pt-spinner"></div>
                                <p className="pt-status-msg">{statusMessage}</p>
                                <p className="pt-poll-info">Vérification {pollCount}/120</p>
                            </div>
                        )}

                        <div className="pt-action-btns">
                            <p className="pt-warning-text">
                                ⚠️ Le paiement a été initié. Veuillez compléter la transaction sur votre téléphone.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentTopup;
